import { useEffect, useState } from 'react';

import type { NewAccount, OperatorOption } from '../../api/monitor';
import type { AccountFieldName } from '../../lib/accounts';
import { accountFields, usesOtp } from '../../lib/accounts';

const EMPTY: Record<AccountFieldName, string> = {
  username: '',
  password: '',
  otpEmail: '',
  otpAppPassword: '',
};

interface NewAccountModalProps {
  operator: OperatorOption;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: NewAccount) => Promise<void>;
}

/**
 * Cadastro de conta.
 *
 * Os campos vêm de `accountFields`, que é onde mora a diferença entre as
 * operadoras — o formulário só desenha o que a operadora aberta pede.
 *
 * O cliente dono da conta (`accounts.customer_id`) não aparece aqui: o servidor
 * o resolve por SGC_CUSTOMER_TOKEN, como a sincronização de masters já faz.
 */
export function NewAccountModal({ operator, saving, onClose, onSubmit }: NewAccountModalProps) {
  const fields = accountFields(operator);
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = async () => {
    if (fields.some((field) => !values[field.name].trim())) {
      setError('Preencha todos os campos da conta.');
      return;
    }

    setError(null);

    try {
      await onSubmit({
        operatorCode: operator.code,
        username: values.username.trim(),
        password: values.password,
        // Operadora sem OTP não manda os campos: o servidor os ignoraria, mas
        // string vazia e "não se aplica" não são a mesma coisa no banco.
        otpEmail: usesOtp(operator) ? values.otpEmail.trim() : null,
        otpAppPassword: usesOtp(operator) ? values.otpAppPassword.trim() : null,
      });
    } catch (cause) {
      setError((cause as Error).message);
    }
  };

  const input =
    'border-line-300 focus:border-navy-800 rounded-[10px] border p-3 text-[14px] outline-none focus:ring-2 focus:ring-[#00244733]';

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-50 bg-[rgb(10_20_45/0.45)]"
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Nova conta ${operator.name}`}
        className="animate-rise-fast fixed top-1/2 left-1/2 z-[51] max-h-[85dvh] w-[min(480px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-[18px] bg-white p-[22px]"
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <div className="text-[16px] font-extrabold">Nova conta — {operator.name}</div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-400 hover:text-ink-900 p-1 text-[20px] leading-none"
          >
            ×<span className="sr-only">Fechar</span>
          </button>
        </div>

        <div className="text-ink-600 mb-3.5 text-[12px]">
          As linhas ficam vinculadas a esta conta no portal da operadora.
        </div>

        <div className="flex flex-col gap-2.5">
          {fields.map((field) => (
            <input
              key={field.name}
              type={field.type}
              inputMode={field.inputMode}
              value={values[field.name]}
              onChange={(event) =>
                setValues((current) => ({ ...current, [field.name]: event.target.value }))
              }
              placeholder={field.placeholder}
              aria-label={field.placeholder}
              autoComplete="off"
              className={`${input} ${field.mono ? 'font-mono' : ''}`}
            />
          ))}

          {usesOtp(operator) ? (
            <div className="text-ink-600 rounded-[10px] bg-[#F7F9FC] px-3 py-2.5 text-[12px] leading-relaxed">
              O token de app permite ler o código OTP que a {operator.name} envia ao Gmail
              durante o login.{' '}
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noreferrer"
                className="text-navy-800 font-bold underline"
              >
                Gerar token de app →
              </a>
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="text-danger-600 mt-2.5 text-[12.5px] font-bold">{error}</div>
        ) : null}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="border-line-300 text-ink-600 flex-1 rounded-[10px] border bg-white p-3 text-[13.5px] font-extrabold"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="bg-navy-800 hover:bg-navy-950 flex-1 rounded-[10px] p-3 text-[13.5px] font-extrabold text-white disabled:opacity-60"
          >
            {saving ? 'Salvando…' : 'Salvar conta'}
          </button>
        </div>
      </div>
    </>
  );
}
